import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllPromoBanners, updatePromoBannerOrder } from "../../actions/promoBannerActions";
import { Link } from "react-router-dom";
import { arrayMoveImmutable } from "array-move";
import SortablePromoBanners from "./SortablePromoBanners/SortablePromoBanners";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableContainer
} from '@mui/material';
import { Add } from "@mui/icons-material";
import MainContentContainer from "../MainContentContainer/MainContentContainer";

const colWidths = {
  col1: "160px",
  colIcon: "56px",
  col2: "320px",
  col0: "180px",
};

const PromoBannersTable = () => {
  const dispatch = useDispatch();
  const banners = useSelector(state => state.banners.banners);
  const [localBanners, setLocalBanners] = useState(null);

  useEffect(() => {
    dispatch(fetchAllPromoBanners());
  }, [dispatch]);

  useEffect(() => {
    if (banners) {
      const sorted = [...banners].sort((a, b) => Number(a.promo_banner_sort_number) - Number(b.promo_banner_sort_number));
      setLocalBanners(sorted);
    }
  }, [banners]);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setLocalBanners(prev => {
      const newOrder = arrayMoveImmutable(prev, oldIndex, newIndex);
      const sort_json = newOrder.map(obj => obj.promo_banner_id);
      dispatch(updatePromoBannerOrder(JSON.stringify(sort_json)));
      return newOrder;
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localBanners.findIndex(item => item.promo_banner_id === active.id);
    const newIndex = localBanners.findIndex(item => item.promo_banner_id === over.id);
    onSortEnd({ oldIndex, newIndex });
  };

  const dragKey = useMemo(() => {
    if (!localBanners) return 'sort-key';
    return localBanners.reduce((accum, val) => (accum += val.promo_banner_id), "sort-key");
  }, [localBanners]);

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Upgrade Banners
          </Typography>
          <Button color="inherit" component={Link} to={`/banners/add`} startIcon={<Add />}>
            Add New
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ mt: 3 }}>
        <TableContainer
          component={Paper}
          sx={{
            marginTop: 3,
            boxShadow: 2,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {!localBanners ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ width: colWidths.col1, fontWeight: 700 }}>Image</TableCell>
                    <TableCell sx={{ width: colWidths.colIcon, fontWeight: 700 }}>Icon</TableCell>
                    <TableCell sx={{ width: colWidths.col2, fontWeight: 700 }}>Title & Description</TableCell>
                    <TableCell sx={{ width: colWidths.col0, fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <SortablePromoBanners
                  items={localBanners}
                  onSortEnd={onSortEnd}
                  widths={colWidths}
                  key={dragKey}
                  rowHover
                />
              </Table>
            </DndContext>
          )}
        </TableContainer>
      </Box>
    </MainContentContainer>
  );
};

export default PromoBannersTable;
